import { Suspense, useDeferredValue, useEffect, useMemo, useState } from 'react'
import { Image, AspectRatio, Stack, Loader } from '@mantine/core'
import { IconChevronDown } from '@tabler/icons-react'
import { Typography } from '../common/typography'
import { useMetrics, useMetricsData } from '../widgets/api'
import { useMatches } from './api'
import { getRankSuffix } from '../common/helpers'
import { TeamLogo } from '../assets/api'
import classes from './Matches.module.css'
import { Fmt } from '../widgets/format'
import { Dropdown } from '../common/components/dropdown/Dropdown'

interface KpiRoundResultsProps {
  competitionId: number
  roundNumber: number | null
  roundStart: string | undefined
  division?: number
}

export default function KpiRoundResults(props: KpiRoundResultsProps) {
  const [selectedKpi, setSelectedKpi] = useState<string | null>(null)
  const deferredSelectedKpi = useDeferredValue(selectedKpi)
  const { data: metrics } = useMetrics()
  const kpis = metrics.filter((m) => m.is_kpi && m.is_used_in_matches)

  useEffect(() => {
    if (kpis.length > 0 && !selectedKpi) {
      setSelectedKpi(kpis[0].key)
    }
  }, [kpis, selectedKpi])

  const kpiSelectData = useMemo(
    () =>
      kpis.map((kpi) => ({
        label: kpi.name,
        value: kpi.key,
      })),
    [kpis]
  )

  return (
    <>
      <div className={classes.kpiRoundHeader}>
        <Typography fw={500} fc={{ light: 'black', dark: 'gray-3' }} lh="125">
          Round KPI Rank
        </Typography>
        <Dropdown
          className={classes.dropdown}
          placeholder="Select KPI"
          value={selectedKpi}
          onChange={(value) => setSelectedKpi(value)}
          data={kpiSelectData}
          rightSection={<IconChevronDown className={classes.select_icon} />}
          styles={{
            root: { maxWidth: '7rem' },
            input: { backgroundColor: 'transparent' },
          }}
        />
      </div>
      {deferredSelectedKpi && (
        <Suspense
          fallback={
            <Stack w="full" h="120px" justify="center" align="center">
              <Loader color="#0B6B7B" />
            </Stack>
          }
        >
          <div
            className={classes.kpiRoundBody}
            style={{
              opacity: deferredSelectedKpi !== selectedKpi ? 0.5 : 1,
            }}
          >
            <KpiRoundResultsInner
              {...props}
              selectedKpi={deferredSelectedKpi}
            />
          </div>
        </Suspense>
      )}
    </>
  )
}

function KpiRoundResultsInner({
  competitionId,
  roundNumber,
  roundStart,
  selectedKpi,
  division,
}: KpiRoundResultsProps & { selectedKpi: string }) {
  const { data: matches } = useMatches({
    competition_id: competitionId,
    round_start: roundNumber || undefined,
    round_end: roundNumber || undefined,
    division,
  })

  // Get unique team IDs from current round matches
  const teamIds = useMemo(() => {
    const uniqueTeamIds = new Set<number>()
    matches.forEach((match) => {
      uniqueTeamIds.add(match.home_team_id)
      if (match.away_team_id) uniqueTeamIds.add(match.away_team_id)
    })
    return Array.from(uniqueTeamIds)
  }, [matches])

  // Create team name and logo maps from matches
  const teamNameMap = useMemo(() => {
    const map = new Map<number, string>()
    matches.forEach((match) => {
      map.set(match.home_team_id, match.home_team_name)
      if (match.away_team_id) map.set(match.away_team_id, match.away_team_name)
    })
    return map
  }, [matches])

  const teamLogoMap = useMemo(() => {
    const map = new Map<number, TeamLogo>()
    matches.forEach((match) => {
      map.set(match.home_team_id, match.home_team_logo)
      if (match.away_team_id) map.set(match.away_team_id, match.away_team_logo)
    })
    return map
  }, [matches])

  const { data: kpiData } = useMetricsData({
    period_type: 'round',
    periods: roundStart ? [roundStart] : [],
    period_range: false,
    subject_type: 'team',
    // teamIds can be empty when the round has no matches (or none in the
    // selected division). An empty list serializes to '' and the API rejects
    // it with "Invalid subject_ids format", so fall back to a placeholder that
    // returns no data. The guard below still renders the panel as null.
    subject_ids: teamIds.length > 0 ? teamIds : [0],
    metrics: selectedKpi ? [selectedKpi] : [],
    components: ['percent'],
    statistics: ['value'],
    competition_id: competitionId,
  })

  // Create a map of team_id -> percent
  // Since we only query for components: ['percent'] and statistics: ['value'],
  // all returned data points match our filter
  const teamPercentMap = useMemo(() => {
    const map = new Map<number, number>()
    kpiData.forEach((data) => {
      map.set(data.subject, data.value)
    })
    return map
  }, [kpiData])

  // Combine teams with their percentages, sort, and add rank
  const teamResults = useMemo(() => {
    return teamIds
      .map((teamId) => ({
        team_id: teamId,
        team_name: teamNameMap.get(teamId) || '',
        team_logo: teamLogoMap.get(teamId),
        percent: teamPercentMap.get(teamId) ?? 0,
      }))
      .sort((a, b) => b.percent - a.percent)
      .map((team, index) => ({
        ...team,
        rank: index + 1,
      }))
  }, [teamIds, teamNameMap, teamLogoMap, teamPercentMap])

  if (!roundStart || teamIds.length === 0) {
    return null
  }

  return (
    <>
      {teamResults.map((result) => (
        <div key={result.team_id} className={classes.kpiRoundRowWrapper}>
          <div>
            <Typography fs="sm" lh="140" ta="right">
              {result.rank}
              <sup>{getRankSuffix(result.rank)}</sup>
            </Typography>
            {result.team_logo && (
              <AspectRatio ratio={1 / 1} className={classes.kpiRoundTeamLogo}>
                <Image
                  src={result.team_logo.mini}
                  alt={result.team_name}
                  fit="scale-down"
                />
              </AspectRatio>
            )}
            <Typography fs="sm" lh="140">
              {result.team_name}
            </Typography>
          </div>
          <Typography fs="sm" fw={500} ff="JetBrains Mono" lh="140">
            <Fmt
              metric={selectedKpi!}
              component="percent"
              statistic="value"
              value={result.percent}
            />
          </Typography>
        </div>
      ))}
    </>
  )
}
